/** FASE 3 · Jun 2026 — Gráficos esenciales del panel financiero (solo Admin). */
import {
  ResponsiveContainer, PieChart, Pie, Cell,
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { useI18n } from "@/context/I18nContext";

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

export function ReceivablesDonut({ collected, receivables }) {
  const { t } = useI18n();
  const total = (collected || 0) + (receivables || 0);
  const pctOf = (v) => (total > 0 ? ((v / total) * 100).toFixed(1) : "0");
  const data = [
    { name: t("fin_kpi_collected"), value: collected || 0, color: "#0F7B3E" },
    { name: t("fin_kpi_receivables"), value: receivables || 0, color: "#B8860B" },
  ];
  return (
    <div data-testid="fin-donut" className="border border-[var(--border)] bg-white p-4">
      <div className="label-overline mb-2">{t("fin_donut_title")}</div>
      {total === 0 ? (
        <div data-testid="fin-donut-empty" className="text-sm italic text-[var(--text-secondary)] py-8">
          {t("fin_donut_empty")}
        </div>
      ) : (
        <>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" strokeWidth={1}>
                  {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v) => money(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="font-mono-tech text-xs space-y-1 mt-2">
            {data.map((d) => (
              <div key={d.name} className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 shrink-0" style={{ background: d.color }} />
                <span>{d.name}: <b>{money(d.value)}</b> ({pctOf(d.value)}%)</span>
              </div>
            ))}
            <div data-testid="fin-donut-total">{t("fin_total")}: <b>{money(total)}</b></div>
          </div>
        </>
      )}
    </div>
  );
}

export function TrendChart({ series, granularity }) {
  const { t } = useI18n();
  const rows = (series || []).map((s) => ({
    ...s,
    label: granularity === "month" ? s.bucket : s.bucket.slice(5),
  }));
  return (
    <div data-testid="fin-trend" className="border border-[var(--border)] bg-white p-4">
      <div className="label-overline mb-2">{t("fin_trend_title")}</div>
      {rows.length === 0 ? (
        <div data-testid="fin-trend-empty" className="text-sm italic text-[var(--text-secondary)] py-8">
          {t("fin_trend_empty")}
        </div>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eeeeee" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`)} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="net_sales_before_tax" name={t("fin_kpi_sales_pretax")} fill="#111111" />
              <Bar dataKey="collected" name={t("fin_kpi_collected")} fill="#0F7B3E" />
              <Line type="monotone" dataKey="known_costs_total" name={t("fin_kpi_costs")} stroke="#B8860B" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
